import React, {
  Fragment,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
  lazy,
  Suspense
} from 'react';
import { QueryClient, QueryClientProvider } from 'react-query';
import { Route, Switch, useHistory } from 'react-router-dom';
import AlertTemplate from 'react-alert-template-basic';
import { transitions, positions, Provider as AlertProvider } from 'react-alert';
// import Login from './pages/Authentication/Login/login.index';
import { ProtectedRoute } from './config/protected-route';
import BusyOverlay from './components/busy-overlay';
import './App.css';
import { useLocation } from 'react-router-dom';
import {
  multipleShipmentLinks,
  seaFreightImportLinks,
  singleShipmentLinks
} from './constants/shipment-links';
import useClearLocalStorage from './utilities/clear-localstorage';
import {
  shopNShipLinks,
  shopNShipUpdateLinks
} from './constants/shopnship-links';
import { frozenFoodsLinks } from './constants/frozen-foods-links';
import { useAuthToken } from './token';
import { useGetUserLazyQuery } from './operations/queries';
import { accountType } from './constants/authentication';
import { getNewUserDate } from './utilities/get-current-date';
import { pathWithoutQuery } from './utilities/path-without-query';
// import ShopNShipOrderNumtUpdate from './components/shop-n-ship-order-num-update';
// import ShopNShipDocumentUpload from './components/shop-n-ship-document-upload';
import ChatWidget from './components/ai-chat-assistant';
// import { useInitClarity } from './utilities/useInitClarity';
import { identifyUser } from './utilities/analytics';
import TrackingOptions from './components/tracking-options';

import { GoogleOAuthProvider } from '@react-oauth/google';
import { loadThirdPartyScripts } from './utilities/third-party-loaders';

const Login = lazy(() => import('./pages/Authentication/Login/login.index'));
const Dashboard = lazy(() => import('./pages/Dashboard/dashboard.index'));
const Signup = lazy(() => import('./pages/Authentication/Signup/signup'));
const OurServices = lazy(() =>
  import('./pages/Dashboard/Services/services.index')
);
const PastShipments = lazy(() =>
  import('./pages/Dashboard/PastShipments/past-shipment-list')
);
const PastShopnShips = lazy(() =>
  import('./pages/Dashboard/PastShopnships/past-shopnship-list')
);
const PastChinaImports = lazy(() =>
  import('./pages/Dashboard/PastChinaImports/past-china-imports')
);
const NewWallet = lazy(() =>
  import('./pages/Dashboard/Wallet/new-wallet.index')
);
const AdditionalCharges = lazy(() =>
  import('./pages/Dashboard/AdditionalCharges/additional-charge')
);
const NewAddresses = lazy(() =>
  import('./pages/Dashboard/Addresses/new-addresses')
);
const NewReferral = lazy(() =>
  import('./pages/Dashboard/Referral/new-referral.index')
);
const Settings = lazy(() =>
  import('./pages/Dashboard/Settings/settings.index')
);
const GuidesAndResourcesPage = lazy(() =>
  import('./pages/Guides&Resources/resources')
);
const Faq = lazy(() => import('./pages/Dashboard/FAQs/faq.index'));
const Profile = lazy(() => import('./pages/Dashboard/Profile/profile.index'));
const ShopNShipDocumentUpload = lazy(() =>
  import('./components/shop-n-ship-document-upload')
);
const ReceiverInfo = lazy(() =>
  import('./pages/Dashboard/Requests/Shipment/receiver-info')
);
const SenderInfo = lazy(() =>
  import('./pages/Dashboard/Requests/Shipment/sender-info')
);
const ItemDescription = lazy(() =>
  import('./pages/Dashboard/Requests/Shipment/item-description')
);
const DeliveryOptions = lazy(() =>
  import('./pages/Dashboard/Requests/Shipment/delivery-options')
);
const OrderSummary = lazy(() =>
  import('./pages/Dashboard/Requests/Shipment/order-summary')
);
const NewShopShipQuote = lazy(() =>
  import('./pages/Dashboard/Requests/Quotes/shopnship-view-quote')
);
const SeaFreightPackageUpload = lazy(() =>
  import(
    './pages/Dashboard/Requests/Shipment/SeaFreight/sea-freight-package-upload'
  )
);
const SeaFreightReceiverInfo = lazy(() =>
  import(
    './pages/Dashboard/Requests/Shipment/SeaFreight/sea-freight-receiver-info'
  )
);
const SeaFreightItemDescription = lazy(() =>
  import(
    './pages/Dashboard/Requests/Shipment/SeaFreight/sea-freight-item-description'
  )
);
const SeaFreightSenderInfo = lazy(() =>
  import(
    './pages/Dashboard/Requests/Shipment/SeaFreight/sea-freight-sender-info'
  )
);
const SeaFreightWelcome = lazy(() =>
  import('./pages/Dashboard/Requests/Shipment/SeaFreight/sea-freight-welcome')
);
const MultipleShopNShipPayment = lazy(() =>
  import(
    './pages/Dashboard/Requests/Shipment/Payment/multiple-shop-n-ship-payment'
  )
);
const ShopnshipShippingInformation = lazy(() =>
  import(
    './pages/Dashboard/Requests/Shipment/BlackFriday/shop-n-ship-shipping-information'
  )
);
const ShopnshipDeliveryAddress = lazy(() =>
  import(
    './pages/Dashboard/Requests/Shipment/BlackFriday/shop-n-ship-delivery-address'
  )
);
const ForeignExchangePayment = lazy(() =>
  import('./pages/Dashboard/Requests/Shipment/Payment/foreign-exchange-payment')
);
const ShopnshipWelcome = lazy(() =>
  import('./pages/Dashboard/Requests/Shipment/BlackFriday/shop-n-ship-welcome')
);
const ShopnshipDeliveryOption = lazy(() =>
  import(
    './pages/Dashboard/Requests/Shipment/BlackFriday/shop-n-ship-delivery-information'
  )
);
const ShopnShipSummary = lazy(() =>
  import('./pages/Dashboard/Requests/Shipment/BlackFriday/shop-n-ship-summary')
);
const ShopnshipPackageInformation = lazy(() =>
  import(
    './pages/Dashboard/Requests/Shipment/BlackFriday/shop-n-ship-package-information'
  )
);
const NotFound = lazy(() => import('./pages/NotFound/notfound'));
const MultipleShipmentPayment = lazy(() =>
  import(
    './pages/Dashboard/Requests/Shipment/Payment/multiple-shipment-payment'
  )
);
const ShopNShipOtherInformation = lazy(() =>
  import(
    './pages/Dashboard/Requests/Shipment/BlackFriday/shop-n-ship-other-information'
  )
);
const ShopNShipItemInformation = lazy(() =>
  import(
    './pages/Dashboard/Requests/Shipment/BlackFriday/shop-n-ship-item-information'
  )
);
const MultipleDeliveryOptions = lazy(() =>
  import('./pages/Dashboard/Requests/Shipment/multiple-delivery-options')
);
const PaymentSuccessfull = lazy(() =>
  import('./components/Commons/success-page')
);
const NewViewQuote = lazy(() =>
  import('./pages/Dashboard/Requests/Quotes/new-view-quote')
);
const MultipleReceiverInfo = lazy(() =>
  import('./pages/Dashboard/Requests/Shipment/multiple-receiver-info')
);
const GetQuotes = lazy(() =>
  import('./pages/Dashboard/Requests/Quotes/get-quotes')
);
const ShopNShipSuccessful = lazy(() =>
  import(
    './pages/Dashboard/Requests/Shipment/BlackFriday/shop-n-ship-successful'
  )
);
const WalletFundingSuccessful = lazy(() =>
  import('./pages/Dashboard/Wallet/FundWallet/wallet-funding-successful')
);
const SignupSuccessful = lazy(() =>
  import('./pages/Authentication/Signup/signup-sucessful')
);
const TermsAndConditions = lazy(() =>
  import('./pages/TermsAndConditions/terms-and-conditions')
);
const FeedbackSuccess = lazy(() =>
  import('./pages/Dashboard/Feedback/feedback-success')
);
const ResetPassword = lazy(() =>
  import('./pages/Authentication/PasswordReset/reset-password')
);
const VerifyMail = lazy(() =>
  import('./pages/Authentication/PasswordReset/verify-mail')
);
const ForgotPassword = lazy(() =>
  import('./pages/Authentication/PasswordReset/forgot-password')
);
const NewTrackingModal = lazy(() =>
  import('./components/modals/new-tracking-modal')
);
const ShopNShipOrderNumtUpdate = lazy(() =>
  import('./components/shop-n-ship-order-num-update')
);
const FrozenFoodsWelcome = lazy(() =>
  import('./pages/Dashboard/Requests/Shipment/FrozenFoods/frozen-foods-welcome')
);
const FrozenFoodsDeliveryInformation = lazy(() =>
  import(
    './pages/Dashboard/Requests/Shipment/FrozenFoods/frozen-foods-pickup-information'
  )
);

const FrozenFoodsItemDescription = lazy(() =>
  import(
    './pages/Dashboard/Requests/Shipment/FrozenFoods/frozen-foods-item-description'
  )
);

const FrozenFoodsOrderSummary = lazy(() =>
  import('./pages/Dashboard/Requests/Shipment/FrozenFoods/frozen-foods-summary')
);

const FrozenFoodsPaymentSuccessful = lazy(() =>
  import(
    './pages/Dashboard/Requests/Shipment/FrozenFoods/frozen-foods-successful'
  )
);

const WeightDiscrepancyPaymentSuccessful = lazy(() =>
  import(
    './pages/Dashboard/Requests/Shipment/Payment/weight-discrepancy-payment-successful'
  )
);

const FrozenFoodsOrderConfirmation = lazy(() =>
  import(
    './pages/Dashboard/Requests/Shipment/FrozenFoods/frozen-foods-order-confirmation'
  )
);

const FrozenFoodsRatesOptions = lazy(() =>
  import(
    './pages/Dashboard/Requests/Shipment/FrozenFoods/frozen-foods-rates-options'
  )
);

const PastFrozenFoods = lazy(() =>
  import('./pages/Dashboard/Requests/Shipment/FrozenFoods/past-frozen-foods')
);

const TrackingDetails = lazy(() => import('./pages/Tracking/TrackingDetails'));

const WeightDiscrepancyPrompt = lazy(() =>
  import('./components/modals/weight-discrepancy-prompt')
);

// Create a client
const queryClient = new QueryClient();
function App() {
  const options = {
    position: positions.TOP_RIGHT,
    timeout: 10000,
    offset: '20px',
    // you can also just use 'scale's
    transition: transitions.SCALE
  };
  const { pathname } = useLocation();
  const history = useHistory();
  const clearStorage = useClearLocalStorage();
  const allShipmentLinks = [
    ...singleShipmentLinks,
    ...multipleShipmentLinks,
    ...shopNShipLinks,
    ...shopNShipUpdateLinks,
    ...seaFreightImportLinks,
    ...frozenFoodsLinks,
    { link: '/login' },
    { link: '/signup' },
    { link: '/request-quote' },
    { link: '/viewquote' },
    { link: '/shipment-request-foreign-exchange-payment' },
    { link: '/shop-and-ship-multiple-payment' },
    {
      link: '/shop-and-ship-payment'
    },
    {
      link: '/frozen-foods-payment'
    },
    {
      link: '/frozen-foods-payment-successful'
    },
    { link: '/shop-and-ship-multiple-payment' },
    { link: '/view-shop-n-ship-quote' }
  ];

  const { search } = useLocation();

  const searchParams = useMemo(() => new URLSearchParams(search), [search]);
  const businessId = localStorage.getItem('lspId');
  const [, , , getToken] = useAuthToken();
  const isLoggedIn = Boolean(getToken());
  const [getUser, { data }] = useGetUserLazyQuery(undefined, undefined, true);
  const [openTrackingModal, setOpenTrackingModal] = useState(true);
  const trackingId = pathname.split('/').pop();

  useEffect(() => {
    if (!data?.user) return;

    function initPendo() {
      if (window.pendo && window.pendo.initialize) {
        window.pendo.initialize({
          visitor: {
            id: `${data?.user?.id}`,
            email: `${data?.user?.email}`
          },

          account: {
            id: `${data?.user?.topshipId}`,
            name: `${data?.user?.fullName}`,
            createdDate: `${new Date(data?.user?.createdDate)}`,
            accountType: `${data?.user?.accountType}`
          }
        });
        return true;
      }
      return false;
    }

    if (initPendo()) return;

    const interval = setInterval(() => {
      if (initPendo()) clearInterval(interval);
    }, 300);

    return () => clearInterval(interval);
  }, [data]);

  // window.pendo.initialize({
  //   visitor: {
  //     id: `${data?.user?.id}`,
  //     email: `${data?.user?.email}`
  //   },

  //   account: {
  //     id: `${data?.user?.topshipId}`,
  //     name: `${data?.user?.fullName}`,
  //     createdDate: `${new Date(data?.user?.createdDate)}`,
  //     accountType: `${data?.user?.accountType}`
  //   }
  // });

  useLayoutEffect(() => {
    async function awaitUser() {
      if (isLoggedIn) {
        await getUser();
      }
    }
    awaitUser();

    //eslint-disable-next-line
  }, [isLoggedIn]);

  useEffect(() => {
    if (
      !allShipmentLinks.find(link => link.link === pathWithoutQuery(pathname))
    ) {
      if (!localStorage.getItem('justPaid')) {
        clearStorage();
      }
    }
    window.scroll(0, 0);

    if (
      !pathname.includes('signup') &&
      isLoggedIn &&
      !pathname.includes('login')
    ) {
      if (data && data?.user) {
        if (businessId !== 'null' && businessId !== null) {
          searchParams.set('lsp', businessId);
        } else {
          searchParams.delete('lsp');
        }

        if (getNewUserDate(data?.user?.createdDate)) {
          if (data?.user?.accountType === accountType[2].value) {
            searchParams.set('isLsp', true);
          } else {
            searchParams.delete('isLsp');
          }
        }
      }
    }
    history.push({
      search: searchParams?.toString()
    });
    // eslint-disable-next-line
  }, [searchParams, pathname, data]);

  useEffect(() => {
    if (isLoggedIn && data) {
      if (window.OneSignal) {
        window.OneSignal.push(function () {
          window.OneSignal.setExternalUserId(data?.user?.topshipId);
        });
      }
    }
  }, [data, isLoggedIn]);

  useEffect(() => {
    if (
      window.location.pathname === '/tracking' ||
      window.location.pathname === `/tracking/details/${trackingId}`
    ) {
      setOpenTrackingModal(true);
    }
  }, [trackingId]);

  useEffect(() => {
    if (data?.user) {
      identifyUser(data.user);
    }
  }, [data]);

  useEffect(() => {
    loadThirdPartyScripts();
  }, []);

  // usePageViews();
  // useInitClarity();

  return (
    <div className='App'>
      <QueryClientProvider client={queryClient}>
        <AlertProvider template={AlertTemplate} {...options}>
          <GoogleOAuthProvider
            clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID}
          >
            <Suspense fallback={<BusyOverlay loading={true} />}>
              <Fragment>
                <Switch>
                  <Route
                    exact
                    path='/terms'
                    component={TermsAndConditions}
                  ></Route>
                  <Route
                    exact
                    path='/policy'
                    component={TermsAndConditions}
                  ></Route>
                  <Route exact path='/login' component={Login}></Route>
                  <Route exact path='/signup' component={Signup}></Route>
                  <Route
                    exact
                    path='/signup-successful'
                    component={SignupSuccessful}
                  ></Route>
                  <Route
                    exact
                    path='/ref/:topshipId'
                    component={Signup}
                  ></Route>
                  <Route
                    exact
                    path='/verify-mail'
                    component={VerifyMail}
                  ></Route>
                  <Route
                    exact
                    path='/password-reset/:token'
                    component={ResetPassword}
                  ></Route>
                  <Route
                    exact
                    path='/forgot-password'
                    component={ForgotPassword}
                  ></Route>
                  <Route exact path='/viewquote' component={NewViewQuote} />
                  <Route
                    exact
                    path='/view-shop-n-ship-quote'
                    component={NewShopShipQuote}
                  />
                  <ProtectedRoute
                    exact
                    path='/'
                    component={Dashboard}
                  ></ProtectedRoute>
                  <Route
                    exact
                    path='/tracking'
                    render={() => (
                      <TrackingOptions
                        openModal={openTrackingModal}
                        setOpenModal={setOpenTrackingModal}
                      />
                    )}
                  ></Route>
                  <Route
                    exact
                    path='/tracking/details/:id'
                    component={TrackingDetails}
                    // render={() => (
                    //   <NewTrackingModal
                    //     trackingId={trackingId}
                    //     openModal={openTrackingModal}
                    //     setOpenModal={setOpenTrackingModal}
                    //   />
                    // )}
                  ></Route>
                  <ProtectedRoute
                    exact
                    path='/wallet'
                    component={NewWallet}
                  ></ProtectedRoute>
                  <ProtectedRoute
                    exact
                    path='/services'
                    component={OurServices}
                  ></ProtectedRoute>
                  <ProtectedRoute
                    exact
                    path='/pending-charges'
                    component={AdditionalCharges}
                  ></ProtectedRoute>
                  <ProtectedRoute
                    exact
                    path='/wallet/wallet-funding-successful'
                    component={WalletFundingSuccessful}
                  ></ProtectedRoute>
                  <Route
                    exact
                    path='/shipment-request-sender-information'
                    component={SenderInfo}
                  ></Route>
                  <Route
                    exact
                    path='/multiple-shipment-request-sender-information'
                    component={SenderInfo}
                  ></Route>
                  <Route
                    exact
                    path='/shipment-request-receiver-information'
                    component={ReceiverInfo}
                  ></Route>
                  <Route
                    exact
                    path='/multiple-shipment-request-receiver-information'
                    component={MultipleReceiverInfo}
                  ></Route>
                  <Route
                    exact
                    path='/shipment-request-item-description'
                    component={ItemDescription}
                  ></Route>
                  <Route
                    exact
                    path='/shipment-request-delivery-options'
                    component={DeliveryOptions}
                  ></Route>
                  <Route
                    exact
                    path='/multiple-shipment-request-delivery-options'
                    component={MultipleDeliveryOptions}
                  ></Route>
                  <Route
                    exact
                    path='/shipment-request-order-summary'
                    component={OrderSummary}
                  ></Route>
                  <Route
                    exact
                    path='/shipment-request-payment'
                    component={OrderSummary}
                  ></Route>
                  <Route
                    exact
                    path='/shipment-request-foreign-exchange-payment'
                    component={ForeignExchangePayment}
                  ></Route>
                  <Route
                    exact
                    path='/multiple-shipment-request-order-summary'
                    component={OrderSummary}
                  ></Route>
                  <Route
                    exact
                    path='/multiple-shipment-request-payment'
                    component={MultipleShipmentPayment}
                  ></Route>
                  <Route
                    exact
                    path='/shop-and-ship-multiple-payment'
                    component={MultipleShopNShipPayment}
                  ></Route>
                  <Route
                    exact
                    path='/shipment-payment-successful'
                    component={PaymentSuccessfull}
                  ></Route>

                  <ProtectedRoute
                    exact
                    path='/past-shipments'
                    component={PastShipments}
                  ></ProtectedRoute>
                  <ProtectedRoute
                    exact
                    path='/past-shopnships'
                    component={PastShopnShips}
                  ></ProtectedRoute>
                  <ProtectedRoute
                    exact
                    path='/past-china-imports'
                    component={PastChinaImports}
                  ></ProtectedRoute>
                  <ProtectedRoute
                    exact
                    path='/past-frozen-foods-orders'
                    component={PastFrozenFoods}
                  ></ProtectedRoute>
                  <ProtectedRoute
                    exact
                    path='/addresses'
                    component={NewAddresses}
                  ></ProtectedRoute>
                  <ProtectedRoute
                    exact
                    path='/shop-and-ship-welcome'
                    component={ShopnshipWelcome}
                  ></ProtectedRoute>

                  <ProtectedRoute
                    exact
                    path='/shop-and-ship-package-information'
                    component={ShopnshipPackageInformation}
                  ></ProtectedRoute>
                  <ProtectedRoute
                    exact
                    path='/shop-and-ship-item-information'
                    component={ShopNShipItemInformation}
                  ></ProtectedRoute>
                  <ProtectedRoute
                    exact
                    path='/shop-and-ship-shipping-information'
                    component={ShopnshipShippingInformation}
                  ></ProtectedRoute>
                  <ProtectedRoute
                    exact
                    path='/shop-and-ship-other-information'
                    component={ShopNShipOtherInformation}
                  ></ProtectedRoute>
                  <ProtectedRoute
                    exact
                    path='/shop-and-ship-delivery-option'
                    component={ShopnshipDeliveryOption}
                  ></ProtectedRoute>
                  <ProtectedRoute
                    exact
                    path='/shop-and-ship-delivery-address'
                    component={ShopnshipDeliveryAddress}
                  ></ProtectedRoute>

                  <ProtectedRoute
                    exact
                    path='/shop-and-ship-payment'
                    component={ShopnShipSummary}
                  ></ProtectedRoute>
                  <ProtectedRoute
                    exact
                    path='/shop-and-ship-summary'
                    component={ShopnShipSummary}
                  ></ProtectedRoute>
                  <ProtectedRoute
                    exact
                    path='/shop-and-ship-upload-documents'
                    component={ShopNShipDocumentUpload}
                  ></ProtectedRoute>
                  <ProtectedRoute
                    exact
                    path='/shop-and-ship-order-tracking-number'
                    component={ShopNShipOrderNumtUpdate}
                  ></ProtectedRoute>
                  <ProtectedRoute
                    exact
                    path='/sea-freight-welcome'
                    component={SeaFreightWelcome}
                  ></ProtectedRoute>
                  <ProtectedRoute
                    exact
                    path='/sea-freight-sender-information'
                    component={SeaFreightSenderInfo}
                  ></ProtectedRoute>
                  <ProtectedRoute
                    exact
                    path='/sea-freight-item-description'
                    component={SeaFreightItemDescription}
                  ></ProtectedRoute>
                  <ProtectedRoute
                    exact
                    path='/sea-freight-receiver-information'
                    component={SeaFreightReceiverInfo}
                  ></ProtectedRoute>
                  <ProtectedRoute
                    exact
                    path='/sea-freight-package-upload'
                    component={SeaFreightPackageUpload}
                  ></ProtectedRoute>
                  <ProtectedRoute
                    exact
                    path='/sea-freight-quote-successful'
                    component={ShopNShipSuccessful}
                  ></ProtectedRoute>
                  <ProtectedRoute
                    exact
                    path='/shop-and-ship-successful'
                    component={ShopNShipSuccessful}
                  ></ProtectedRoute>
                  <ProtectedRoute
                    exact
                    path='/frozen-foods-welcome'
                    component={FrozenFoodsWelcome}
                  ></ProtectedRoute>
                  <ProtectedRoute
                    exact
                    path='/frozen-foods-pickup-information'
                    component={FrozenFoodsDeliveryInformation}
                  ></ProtectedRoute>
                  <ProtectedRoute
                    exact
                    path='/frozen-foods-item-information'
                    component={FrozenFoodsItemDescription}
                  ></ProtectedRoute>
                  <ProtectedRoute
                    exact
                    path='/frozen-foods-rates-options'
                    component={FrozenFoodsRatesOptions}
                  ></ProtectedRoute>
                  <ProtectedRoute
                    exact
                    path='/frozen-foods-summary'
                    component={FrozenFoodsOrderSummary}
                  ></ProtectedRoute>
                  <ProtectedRoute
                    exact
                    path='/frozen-foods-payment'
                    component={FrozenFoodsOrderSummary}
                  ></ProtectedRoute>
                  <ProtectedRoute
                    exact
                    path='/frozen-foods-payment-successful'
                    component={FrozenFoodsPaymentSuccessful}
                  ></ProtectedRoute>
                  <ProtectedRoute
                    exact
                    path='/frozen-foods-order-confirmation'
                    component={FrozenFoodsOrderConfirmation}
                  ></ProtectedRoute>

                  <ProtectedRoute
                    exact
                    path='/weight-discrepancy-payment-successful'
                    component={WeightDiscrepancyPaymentSuccessful}
                  ></ProtectedRoute>

                  <ProtectedRoute
                    exact
                    path='/feedback-received'
                    component={FeedbackSuccess}
                  ></ProtectedRoute>
                  <ProtectedRoute
                    exact
                    path='/referral'
                    component={NewReferral}
                  ></ProtectedRoute>
                  <Route
                    exact
                    path='/request-quote'
                    component={GetQuotes}
                  ></Route>
                  <ProtectedRoute
                    exact
                    path='/faq'
                    component={Faq}
                  ></ProtectedRoute>

                  <ProtectedRoute
                    exact
                    path='/profile'
                    component={Profile}
                  ></ProtectedRoute>
                  {/* <ProtectedRoute
                exact
                path='/vib'
                component={VIB}
              ></ProtectedRoute> */}
                  <ProtectedRoute
                    exact
                    path='/settings'
                    component={Settings}
                  ></ProtectedRoute>
                  <ProtectedRoute
                    exact
                    path='/guides-and-resources'
                    component={GuidesAndResourcesPage}
                  ></ProtectedRoute>
                  <Route
                    render={props => <NotFound heading='Page not found' />}
                  />
                </Switch>
              </Fragment>
              {data?.user && <WeightDiscrepancyPrompt />}
            </Suspense>
          </GoogleOAuthProvider>
        </AlertProvider>
        <BusyOverlay />
        <ChatWidget />
      </QueryClientProvider>
    </div>
  );
}

export default App;
